import { useMemo, useState } from "react"
import { computed } from "mobx"
import { observer } from "mobx-react-lite"
import { Button, Flex } from "antd"
import {
  CopyOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from "@ant-design/icons"
import Tippy from "@tippyjs/react"

import { DeleteConfirm } from "@src/app/components/DeleteConfirm"
import { IconCopy } from "@src/app/components/icons/copy"
import { notify } from "@src/app/components/notify"
import { TokensCollection } from "@src/pages/settingsApiKeys/classes/TokensCollection"
import { WithCollection } from "@src/shared/classes/Collection/types"
import css from "./index.module.css"

type Props = WithCollection<TokensCollection> & {
  id: string
}
export const TokensTableActions = observer(({ collection, id }: Props) => {
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false)

  const isVisible = computed(() => {
    return collection.isVisibleId(id)
  }).get()

  const icon = useMemo(
    () => (isVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />),
    [isVisible],
  )

  const copyText = (id: string): void => {
    navigator.clipboard.writeText(id)

    notify.success("Copied")
  }

  const onDeleteConfirm = () => {
    collection.delete(id).finally(() => {
      notify.success("Token has been deleted")

      setOpenDeleteConfirm(false)
    })
  }

  return (
    <Flex justify="center">
      <Button
        icon={icon}
        onClick={() => collection.toggleIsVisibleId(id)}
        size="small"
        type="text"
      />

      <Tippy className={css.tooltip} content="Copy to clipboard">
        <Button
          icon={<IconCopy height={14} width={14} />}
          onClick={() => copyText(id)}
          size="small"
          type="text"
        />
      </Tippy>

      <Button
        icon={<DeleteOutlined />}
        loading={collection.isLoading}
        onClick={() => setOpenDeleteConfirm(true)}
        size="small"
        type="text"
      />
      <DeleteConfirm
        cancelText="Cancel"
        isLoading={collection.isLoading}
        okText="Delete Key"
        onCancel={() => setOpenDeleteConfirm(false)}
        onOk={onDeleteConfirm}
        open={openDeleteConfirm}
        text="This action cannot be undone"
        title="Are you sure you want to delete this key?"
      />
    </Flex>
  )
})
